import React, { useEffect, useRef, useState } from 'react';
import { withTranslation, WithTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';
import { Button, Icon } from '@material-ui/core';
import App from '../../../components/app';
import EnhancedForm from '../../../components/form';
import GoogleMapSelectPolygon from '../../../components/google-map-select-polygon';
import { Dialog } from '../../../components/dialog';
import withGlobalStyles from '../../../utils/global-styles';
import { TooltipButton } from '../../../components/tooltip-button';
import { FormDatas } from '../../../local-types';
import { clsx } from '../../../utils/htmlUtils';
import MapUtils from '../../../utils/mapUtils';

import {
    mapFormToRankDTO,
    RankFormData,
    selectors as rankSelectors,
    types as rankTypes,
} from '../../../redux/reducers/rank';
import { useSelector } from 'react-redux';
import DateUtils from '../../../utils/dateUtils';
import { useRankService } from '../../../redux/reducers/rank/service';
import ErrorMessage from '../../../components/error-message';
import { useRankError } from './useRankError';
import { selectors as pendingSelectors } from '../../../redux/reducers/pendingOperations/module';
import { AccountRight, RightRank } from '../../../utils/userUtils';
import { paths } from '../../paths';

interface RankFormPageProps extends WithTranslation {
    globalClasses: any;
}

const RankFormPage = ({ t, globalClasses }: RankFormPageProps): JSX.Element => {
    const history = useHistory();
    const { id } = useParams<{ id?: string }>();

    const { getRank, getArea, getDispatchGroups, createRank, updateRank, deleteRank } = useRankService();
    const [dialogType, setDialogType] = useState<'create' | 'update' | 'delete' | null>(null);
    const dialogConfirmActionRef = useRef(null);
    const dialogConfirmDeleteRef = useRef(null);

    const { loadingRank } = useSelector((state: any): { loadingRank: boolean } => ({
        loadingRank: pendingSelectors.isLoadingRank(state),
    }));
    const isSavingRank = useSelector((state: any): boolean =>
        pendingSelectors.isPending(state, rankTypes.CREATE_RANK, rankTypes.UPDATE_RANK),
    );

    const rank = useSelector((state) => rankSelectors.rankById(state, id));
    const area = useSelector(rankSelectors.area);
    const dispatchGroups = useSelector(rankSelectors.dispatchGroups);

    const [formData, setFormData] = useState<RankFormData | undefined>(undefined);
    const { errorPayload, validateAndSubmit } = useRankError();

    const isEditMode = React.useMemo(() => !!id, [id]);

    useEffect(() => {
        if (isEditMode) {
            getRank(id);
            getArea(id);
        }
        getDispatchGroups();
    }, [isEditMode, id, getRank, getArea, getDispatchGroups]);

    useEffect(() => {
        if (isEditMode && rank && area && dispatchGroups.length > 0) {
            const selectedDGs = dispatchGroups.filter((dg) => rank.dg?.some((rdg) => rdg.name === dg.id));

            let dateStart = '';
            let dateEnd = '';
            let noTimeLimit = false;

            if (rank.start_datetime) {
                dateStart = DateUtils.toLocalDatetimeInput(rank.start_datetime);
            }
            if (rank.end_datetime) {
                dateEnd = DateUtils.toLocalDatetimeInput(rank.end_datetime);
            }

            // If there is a start date but no end date, check "No time limit"
            if (rank.start_datetime && !rank.end_datetime) {
                noTimeLimit = true;
            }

            setFormData({
                name: rank.name || '',
                'date-start': dateStart,
                'date-end': dateEnd,
                polygon: area.polygon || [],
                dispatch_group: selectedDGs,
                no_time_limit: noTimeLimit,
            });
        } else if (!isEditMode) {
            setFormData({
                name: '',
                'date-start': '',
                'date-end': '',
                polygon: [],
                dispatch_group: [],
            });
        }
    }, [isEditMode, id, rank, area, dispatchGroups]);

    const handlePolygonReset = () => setFormData((prev) => ({ ...prev, polygon: [] }));

    const generateInputs = (): FormDatas[] => [
        {
            title: t('generales_informations'),
            sectionStyle: { flex: 1 },
            datas: [
                {
                    id: 'name',
                    label: `${t('common:form.lastname')} (${t('common:driver_visible')})`,
                    required: true,
                },
                {
                    id: 'dispatch_group',
                    label: t('rank:dispatch_group'),
                    boxes: dispatchGroups.sort((a, b) => a.name.localeCompare(b.name)),
                },
                {
                    id: 'date-start',
                    label: t('common:form.start_date'),
                    type: 'datetime-local',
                    required: true,
                    inputsAttributs: {
                        min: isEditMode
                            ? undefined
                            : DateUtils.toLocalDatetimeInput(DateUtils.todayAtMidnight().toString()),
                        max: DateUtils.toLocalDatetimeInput(DateUtils.addYears(1).toString()),
                    },
                    linked: [{ id: 'no_time_limit', value: false }],
                },
                {
                    id: 'date-end',
                    label: t('common:form.end_date'),
                    type: 'datetime-local',
                    required: true,
                    linkDate: [
                        { id: 'date-start', type: 'min', value: 0, next: false, isFullDate: true },
                        { id: 'date-start', type: 'max', value: 365, next: true, isFullDate: true },
                    ],
                    linked: [{ id: 'no_time_limit', value: false }],
                },
                {
                    id: 'no_time_limit',
                    label: t('rank:no_time_limit'),
                    type: 'boolean',
                    inputsAttributs: { reverse: true },
                },
            ],
        },
        {
            title: t('address_definition'),
            sectionStyle: { flex: 2 },
            datas: [
                {
                    id: 'polygones',
                    label: t('areas.polygon'),
                    required: true,
                    customView: () => (
                        <GoogleMapSelectPolygon
                            globalClasses={globalClasses}
                            center={MapUtils.defaultCenter}
                            zoom={MapUtils.defaultZoom}
                            polygon={formData?.polygon ?? []}
                            onPolygonChange={(polygon) => setFormData({ ...formData, polygon })}
                        />
                    ),
                },
            ],
        },
    ];

    const onSubmit = (values: any) => {
        if (!formData) return;

        // If "No time limit" is checked, set the dates automatically
        if (values.no_time_limit) {
            values['date-start'] = Math.floor(new Date().getTime() / 1000);
            values['date-end'] = null;
        }

        const mergedData: RankFormData = {
            ...values,
            polygon: formData.polygon,
        };
        const dto = mapFormToRankDTO(mergedData);
        if (isEditMode) {
            validateAndSubmit(dto, (dto) =>
                updateRank(id, dto, () => {
                    setDialogType('update');
                    dialogConfirmActionRef.current?.showDialog();
                }),
            );
        } else {
            validateAndSubmit(dto, (dto) =>
                createRank(dto, () => {
                    setDialogType('create');
                    dialogConfirmActionRef.current?.showDialog();
                }),
            );
        }
    };

    const handleDialogOK = () => {
        dialogConfirmActionRef.current?.closeDialog();
        history.push('/ranks');
    };

    const handleDeleteClick = () => {
        dialogConfirmDeleteRef.current?.showDialog();
    };

    const handleDeleteConfirm = () => {
        dialogConfirmDeleteRef.current?.closeDialog();
        if (id) {
            deleteRank(id, () => {
                setDialogType('delete');
                dialogConfirmActionRef.current?.showDialog();
            });
        }
    };

    const getDialogDescription = () => {
        if (dialogType === 'create') return t('rank:create_success');
        if (dialogType === 'update') return t('rank:update_success');
        if (dialogType === 'delete') return t('rank:delete_success');
        return '';
    };

    const handleDeleteCancel = () => {
        dialogConfirmDeleteRef.current?.closeDialog();
    };

    return (
        <App needRights={{ name: AccountRight.DRIVER_PREBOOK, mode: RightRank.W }} loading={loadingRank}>
            <ErrorMessage payload={errorPayload} />
            {formData && (
                <EnhancedForm
                    payload={formData}
                    inputs={generateInputs()}
                    onReset={handlePolygonReset}
                    onSubmit={onSubmit}
                    backRoute={paths.ranks}
                    loader={isSavingRank}
                    onRemove={
                        isEditMode ? (
                            <TooltipButton title={t('common:deleteForever')}>
                                <Button
                                    startIcon={<Icon>delete_forever</Icon>}
                                    variant="contained"
                                    className={clsx(globalClasses.roundButton, globalClasses.btnDanger)}
                                    onClick={handleDeleteClick}
                                >
                                    {t('common:delete')}
                                </Button>
                            </TooltipButton>
                        ) : undefined
                    }
                />
            )}
            <Dialog
                onRef={(ref) => (dialogConfirmActionRef.current = ref)}
                dialogTitle={t('common:success')}
                description={getDialogDescription()}
                btnOKText={t('common:ok')}
                onOK={handleDialogOK}
                noKOButton
            />
            <Dialog
                onRef={(ref) => (dialogConfirmDeleteRef.current = ref)}
                dialogTitle={t('common:delete')}
                description={t('rank:delete_confirm')}
                btnOKText={t('common:delete')}
                btnKOText={t('common:cancel')}
                onOK={handleDeleteConfirm}
                onKO={handleDeleteCancel}
            />
        </App>
    );
};

export default withGlobalStyles(withTranslation(['rank', 'common'])(RankFormPage));
